import { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  Box,
  SelectChangeEvent,
  Theme,
} from '@mui/material';
import {
  AggregationColumn,
  AggregationItem,
  IssueTypeField,
  JiraComponent,
  JiraEpic,
  JiraIssueType,
  JiraPriority,
  JiraProject,
  JiraTicket,
  JiraUser,
  Nullable,
} from 'common-types';
import CreateJiraTicketBaseFields from './CreateJiraTicketBaseFields';
import CreateJiraTicketDescriptionFields from './CreateJiraTicketDescriptionFields';
import CreateJiraTicketAdvancedFields from './CreateJiraTicketAdvancedFields';
import CreateJiraTicketUserSearchFields from './CreateJiraTicketUserSearchFields';

const CreateJiraTicketContent: FC<
  React.PropsWithChildren<CreateJiraTicketContentProps>
> = ({
  onIssueTypeChange,
  onInputChange,
  onProjectChange,
  selectedAggregationsProps,
  onJiraTicketFormChange,
  onDynamicFieldChange,
  jiraTicketFields,
  projects,
  issueTypes,
  reporters,
  assigness,
  components,
  epics,
  labels,
  prioritis,
  loadingProjects,
  loadingAssignees,
  loadingReporters,
  loadingDependentByProjectFields,
  loadingComponents,
  loadingEpics,
  fieldsMetadata,
  dynamicTypeFieldsMetadata,
  onFieldChange,
  validationObject,
}) => {
  const { classes } = useStyles();

  return (
    <Box
      component='form'
      noValidate
      autoComplete='off'
      className={classes.createJiraTicketForm}>
      <CreateJiraTicketBaseFields
        validationObject={validationObject}
        fieldsMetadata={fieldsMetadata}
        loadingDependentByProjectFields={loadingDependentByProjectFields}
        loadingProjects={loadingProjects}
        jiraTicketFields={jiraTicketFields}
        projects={projects}
        issueTypes={issueTypes}
        onIssueTypeChange={onIssueTypeChange}
        onJiraTicketFormChange={onJiraTicketFormChange}
        onProjectChange={onProjectChange}
        onInputChange={onInputChange}
      />
      <CreateJiraTicketUserSearchFields
        assigness={assigness}
        reporters={reporters}
        loadingAssignees={loadingAssignees}
        loadingReporters={loadingReporters}
        jiraTicketFields={jiraTicketFields}
        fieldsMetadata={fieldsMetadata}
        validationObject={validationObject}
        onInputChange={onInputChange}
        onFieldChange={onFieldChange}
      />
      <CreateJiraTicketAdvancedFields
        onDynamicFieldChange={onDynamicFieldChange}
        prioritis={prioritis}
        epics={epics}
        loadingEpics={loadingEpics}
        validationObject={validationObject}
        onInputChange={onInputChange}
        fieldsMetadata={fieldsMetadata}
        dynamicTypeFieldsMetadata={dynamicTypeFieldsMetadata}
        jiraTicketFields={jiraTicketFields}
        components={components}
        labels={labels}
        onFieldChange={onFieldChange}
        loadingComponents={loadingComponents}
        onJiraTicketFormChange={onJiraTicketFormChange}
      />
      <CreateJiraTicketDescriptionFields
        selectedAggregationsProps={selectedAggregationsProps}
        onJiraTicketFormChange={onJiraTicketFormChange}
        jiraTicketFields={jiraTicketFields}
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  createJiraTicketForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
}));

export interface CreateJiraTicketContentProps {
  onIssueTypeChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: Nullable<Partial<JiraIssueType>>,
    reason: AutocompleteChangeReason,
  ) => void;
  onProjectChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: Nullable<JiraProject>,
    reason: AutocompleteChangeReason,
  ) => void;
  onInputChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: Nullable<string>,
    reason: AutocompleteInputChangeReason,
  ) => void;
  onJiraTicketFormChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
  ) => void;
  onDynamicFieldChange: (
    e: SelectChangeEvent<{
      value: string;
      id: string;
    }>,
  ) => void;
  onFieldChange: (
    fieldName: string,
  ) => (
    _e: React.SyntheticEvent<Element, Event>,
    value:
      | Nullable<JiraComponent[]>
      | Nullable<JiraUser>
      | Nullable<string[]>
      | Nullable<JiraEpic>,
  ) => void;
  fieldsMetadata: Nullable<{
    [key: string]: IssueTypeField;
  }>;
  dynamicTypeFieldsMetadata: Nullable<IssueTypeField[]>;
  loadingDependentByProjectFields: boolean;
  loadingProjects: boolean;
  loadingAssignees: boolean;
  loadingReporters: boolean;
  loadingComponents: boolean;
  loadingEpics: boolean;
  jiraTicketFields: JiraTicket;
  projects: Nullable<JiraProject[]>;
  issueTypes: Nullable<JiraIssueType[]>;
  assigness: Nullable<JiraUser[]>;
  reporters: Nullable<JiraUser[]>;
  epics: Nullable<JiraEpic[]>;
  components: Nullable<JiraComponent[]>;
  prioritis: Nullable<JiraPriority[]>;
  labels: Nullable<string[]>;
  selectedAggregationsProps?: {
    info: string;
    config: { withSelection: boolean; columns: AggregationColumn[] };
    items: AggregationItem[];
  };
  validationObject: Nullable<{
    [key: string]: {
      message: string;
    };
  }>;
}

export default CreateJiraTicketContent;
